"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Form,
} from "./ui/form";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { useState } from "react";
import { Loading } from "@/icons/Loading";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { ErrorType } from "@/app/types/contact-errors";

const formSchema = z.object({
  name: z.string().min(2).max(255),
  email: z.string().email(),
  message: z.string().min(1).max(1000),
});

export function ContactForm() {
  const [formState, setFormState] = useState<
    | "idle"
    | "submitting"
    | "success"
    | "captcha-fail"
    | "captcha-score"
    | "fail"
    | "ses-fail"
  >("idle");

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
    },
  });

  const { executeRecaptcha } = useGoogleReCaptcha();
  async function onSubmit(values: z.infer<typeof formSchema>) {
    setFormState("submitting");
    if (!executeRecaptcha) throw new Error("Recaptcha not initialised");
    const captchaToken = await executeRecaptcha("inquirySubmit");

    const res = await fetch("/api/contact", {
      method: "POST",
      body: JSON.stringify({ ...values, captcha: captchaToken }),
    });
    const data = await res.json();
    if (data.success) {
      setFormState("success");
    } else {
      switch (data.error) {
        case ErrorType.RECAPTCHA:
          setFormState("fail");
          console.error("Recaptcha did not run", data.message);
          break;
        case ErrorType.RECAPTCHA_SCORE:
          setFormState("fail");
          console.error("Recaptcha score too low");
          break;
        case ErrorType.SES:
          setFormState("fail");
          console.error("SES error", data.message);
        default:
          setFormState("fail");
          console.error("Unknown error", data);
      }
    }
  }
  return (
    <>
      {formState !== "success" ? (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-8 font-sans text-lg"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-sans font-bold text-2xl text-sima-orange">
                    Name
                  </FormLabel>
                  <FormControl className="text-zinc-900 text-xl">
                    <Input
                      placeholder="Your full name"
                      className=""
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-sans font-bold text-2xl text-sima-orange">
                    Email
                  </FormLabel>
                  <FormControl className="text-zinc-950 text-xl">
                    <Input placeholder="Your email address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-sans font-bold text-2xl text-sima-orange">
                    Message
                  </FormLabel>
                  <FormControl className="text-zinc-900 text-xl">
                    <Textarea
                      placeholder="Please give us some details about your project and your needs
                and we'll do our very best to assist you."
                      rows={6}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              className="text-lg md:text-xl font-bold bg-sima-orange p-6 rounded-2xl  shadow-md hover:shadow-none hover:bg-sima-orange"
              type="submit"
              disabled={formState === "submitting"}
            >
              {formState === "submitting" ? (
                <Loading className="w-6 h-6 " />
              ) : (
                "Submit"
              )}
            </Button>
          </form>
          {formState === "fail" && (
            <p className="py-4 text-red-600">
              Submission failed, please check your details and try again
            </p>
          )}
          {formState === "captcha-fail" && (
            <p className="py-4 text-red-600">
              Recaptcha failed to run, please try again
            </p>
          )}
          {formState === "captcha-score" && (
            <p className="py-4 text-red-600">
              Recaptcha failed, please try again later or email
            </p>
          )}
          {formState === "ses-fail" && (
            <p className="py-4 text-red-600">
              Submission failed, please check your details and try again
            </p>
          )}
        </Form>
      ) : (
        <div className="flex flex-col gap-4 text-xl py-2">
          <p className="w-full md:w-2/3">
            Thank you for your message. We will be in touch shortly.
          </p>
        </div>
      )}
    </>
  );
}
